import { shape, string, array, object, bool, number } from 'prop-types'
import { graphql } from 'gatsby'
import Product from '@/components/Product/'
import SEO from '@/components/SEO'
import Layout from '@/layout'
import mediaQueries from '@/layout/media-queries'
import { OnboardingProvider } from '@/context/OnboardingContext'
import {
  getSeoTitle,
  getSeoDesc,
  getDesignationLabel,
  generateJsonLinkingDataAppSchema,
  getEmptyFeedback,
} from '@/utils/products'
import withRedirectForBannedApps from '@/hocs/withRedirectForBannedApps'

const overrideStyles = {
  container: {
    marginBottom: '60px',

    [mediaQueries.smallScreen()]: {
      marginBottom: '0px',
    },
  },
}

const AppTemplate = ({ data: { app, feedback, developer } }) => {
  const { id, product } = app
  feedback = feedback || getEmptyFeedback(id)
  const designationLabel = getDesignationLabel(product)

  return (
    <OnboardingProvider id={id}>
      <Layout overrideStyles={overrideStyles} app={app} headerLabel={app.name}>
        <Product
          product={app}
          feedback={feedback}
          developer={developer}
          designation={designationLabel}
        />
      </Layout>
    </OnboardingProvider>
  )
}

AppTemplate.propTypes = {
  data: shape({
    app: shape({
      id: string,
      name: string,
      shortDescription: string,
      screenshots: array,
      hidden: bool,
      slug: string,
    }),
    feedback: shape({
      id: string,
      rate: number,
      votes: number,
      histogram: object,
      reviews: array,
    }),
  }),
}

/* eslint-disable react/prop-types */
export const Head = ({ data: { app, feedback } }) => {
  const { name, shortDescription, screenshots, id, hidden, slug, product } = app
  const designationLabel = getDesignationLabel(product)
  feedback = feedback || getEmptyFeedback(id)

  return (
    <SEO
      title={getSeoTitle(name, designationLabel)}
      description={getSeoDesc(id, shortDescription)}
      type={`${designationLabel} App`}
      typeLabel="Type"
      schemaMarkup={generateJsonLinkingDataAppSchema(app, feedback)}
      image={screenshots?.[0]?.large}
      path={`/apps/${slug}/`}
      noIndex={hidden}
    />
  )
}
/* eslint-enable react/prop-types */

export default withRedirectForBannedApps(AppTemplate)

export const query = graphql`
  query ($appId: String!, $ownerId: String!) {
    app(id: { eq: $appId }) {
      logo {
        ...ProductLogo
      }
      images {
        ...ProductImage
      }
      icons {
        id
        small
        original
        large
        title
      }
      screenshots {
        id
        small
        original
        large
        title
      }
      id
      slug
      name
      product
      hidden
      hasInAppUpgrades
      underMaintenance
      shortDescription
      longDescription
      ownerId
      changelog
      authorization {
        clientId
      }
      tutorialUrl
      privacyPolicyUrl
      payment {
        price
        frequency
        trialDays
        perAccount
      }
      features {
        description
        title
      }
      benefits {
        description
        title
      }
      category {
        name
        slug
      }
      customProps {
        usingBeta
        appVideoUrl
        inAppPurchases
        installationsOffset
        isPremium
        verified
      }
      installations
      elements {
        cover {
          title
          image
          color
        }
        widgets {
          placement
        }
      }
      widgets {
        id
        placement
      }
    }

    feedback(product: { eq: $appId }) {
      rate
      product
      id
      reviews {
        text
        rate
        name
        createdAt
      }
      votes
      histogram {
        _1 {
          percent
          votes
        }
        _2 {
          percent
          votes
        }
        _3 {
          percent
          votes
        }
        _4 {
          percent
          votes
        }
        _5 {
          percent
          votes
        }
      }
    }

    developer(id: { eq: $ownerId }) {
      id
      name
      company
      slug
      contactEmail
      apps {
        logo {
          ...ProductLogo
        }
        icons {
          large
          original
          small
          title
        }
        id
        name
        product
        payment {
          creditCardRequired
          frequency
          perAccount
          price
          trialDays
        }
        shortDescription
        slug
      }
      services {
        logo {
          ...ProductLogo
        }
        icons {
          large
          original
          small
          title
        }
        id
        name
        product
        payment {
          creditCardRequired
          frequency
          perAccount
          price
          trialDays
        }
        shortDescription
        slug
        customProps {
          isService
        }
      }
    }
  }
`
